(() => {
  const toggle = document.querySelector('.rwady-quick-browse-toggle');
  if (!toggle) return;
  const root = document.documentElement;
  let compact = document.querySelector('#ProductGridContainer .rwady-page-controls')?.dataset.pageSize === '100';
  let modalOpen = false;
  let origin = null;
  let scheduled = false;
  // Use the optimized layout path on collections and search, including localized URLs.
  const optimizeLayout = /\/(?:collections\/[^/]+|search)\/?$/.test(window.location.pathname);
  let positionScheduled = false;
  let positioned = false;
  let toggleSize = null;
  // Keep the button out of paint until its first coordinates are ready.
  if (optimizeLayout) toggle.style.visibility = 'hidden';
  const queuePosition = () => {
    if (positionScheduled) return;
    positionScheduled = true;
    requestAnimationFrame(() => {
      positionScheduled = false;
      if (!toggle.hidden) positionToggle();
    });
  };
  let changingPageSize = false;
  let loadFailed = false;
  function useQuickBrowsePageSize() {
    if (changingPageSize) return false;
    const container = document.getElementById('ProductGridContainer');
    const controls = container?.querySelector('.rwady-page-controls');
    const size = compact ? '100' : '25';
    if (!controls || controls.dataset.pageSize === size) return false;
    const scopedRequest = window.RwadyCollectionSearchController?.handlePageSize?.(size, controls.dataset.viewPrefix);
    if (scopedRequest) {
      changingPageSize = true;
      loadFailed = false;
      const clearPlaceholders = size === '100' ? fillBrowsePlaceholders(container, controls) : () => {};
      sync();
      Promise.resolve(scopedRequest).then(success => { loadFailed = success === false; }).finally(() => {
        clearPlaceholders();
        changingPageSize = false;
        compact = document.querySelector('#ProductGridContainer .rwady-page-controls')?.dataset.pageSize === '100';
        document.dispatchEvent(new CustomEvent('rwady:browse-mode-changed'));
        sync();
      });
      return true;
    }
    const startUrl = window.location.href;
    const target = new URL(startUrl);
    target.searchParams.set('view', controls.dataset.viewPrefix + size);
    ['page', 'section_id', 'sections'].forEach(key => target.searchParams.delete(key));
    changingPageSize = true;
    loadFailed = false;
    container.setAttribute('aria-busy', 'true');
    sync();
    loadBrowsePage(container, controls, target, startUrl, size);
    return true;
  }
  function fillBrowsePlaceholders(container, controls) {
    const grid = container.querySelector('.rwady-collection-product-grid, .rwady-search-results-grid');
    if (!grid) return () => {};
    const existing = grid.querySelectorAll('.rwady-compact-card').length;
    const rawTotal = controls.querySelector('[data-total]')?.dataset.total;
    const total = rawTotal == null ? 100 : Number(rawTotal);
    const count = Math.max(0, Math.min(100, Number.isFinite(total) ? total : 100) - existing);
    const countLabel = controls.querySelector('.rwady-page-count');
    const sizeSelect = controls.querySelector('.rwady-page-size-select');
    const oldLabel = countLabel?.textContent;
    const oldSize = sizeSelect?.value;
    const oldDisabled = sizeSelect?.disabled;
    const targetCount = Math.min(100, Number.isFinite(total) ? total : 100);
    if (countLabel) countLabel.textContent = document.documentElement.lang.startsWith('ar')
      ? 'جاري تحميل ' + targetCount + ' منتج…'
      : 'Loading ' + targetCount + ' products…';
    if (sizeSelect) { sizeSelect.value = '100'; sizeSelect.disabled = true; }
    const nodes = [];
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => { entry.target.dataset.visible = String(entry.isIntersecting); });
    }) : null;
    const visibility = () => nodes.forEach(node => { node.dataset.pageHidden = String(document.hidden); });
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const node = document.createElement(grid.tagName === 'UL' ? 'li' : 'div');
      node.className = 'rwady-browse-placeholder';
      node.setAttribute('aria-hidden', 'true');
      node.dataset.visible = String(!observer);
      nodes.push(node);
      fragment.append(node);
    }
    grid.append(fragment);
    nodes.forEach(node => observer?.observe(node));
    visibility();
    document.addEventListener('visibilitychange', visibility);
    return () => {
      if (countLabel) countLabel.textContent = oldLabel;
      if (sizeSelect) { sizeSelect.value = oldSize; sizeSelect.disabled = oldDisabled; }
      observer?.disconnect();
      document.removeEventListener('visibilitychange', visibility);
      nodes.forEach(node => node.remove());
    };
  }
  async function loadBrowsePage(container, controls, target, startUrl, size) {
    const clearPlaceholders = size === '100' ? fillBrowsePlaceholders(container, controls) : () => {};
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(target.href, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Browse request failed');
      const html = await response.text();
      if (window.location.href !== startUrl || !container.isConnected || !controls.isConnected) return;
      const page = new DOMParser().parseFromString(html, 'text/html');
      const next = page.getElementById('ProductGridContainer');
      if (!next || next.querySelector('.rwady-page-controls')?.dataset.pageSize !== size) throw new Error('Invalid browse page');
      const oldSection = container.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
      const newSection = next.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
      let markup = next.innerHTML;
      if (oldSection && newSection && oldSection !== newSection) markup = markup.split(newSection).join(oldSection);
      // Keep decoded images when the page-size response recreates the same products.
      const loadedImages = new Map();
      container.querySelectorAll('.rwady-compact-card').forEach(button => {
        const image = button.querySelector('img');
        if (image?.complete && image.naturalWidth > 0) {
          loadedImages.set(button.dataset.quickProductId, image);
        }
      });
      clearPlaceholders();
      container.innerHTML = markup;
      container.querySelectorAll('.rwady-compact-card').forEach(button => {
        const image = button.querySelector('img');
        const loaded = loadedImages.get(button.dataset.quickProductId);
        if (image && loaded &&
            ['src', 'srcset', 'sizes'].every(name => image.getAttribute(name) === loaded.getAttribute(name))) {
          image.replaceWith(loaded);
        }
      });
      history.replaceState({ ...history.state, searchParams: target.searchParams.toString() }, '', target.href);
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
      document.dispatchEvent(new CustomEvent('rwady:browse-mode-changed'));
    } catch (_) {
      loadFailed = true;
    } finally {
      clearPlaceholders();
      clearTimeout(timeout);
      changingPageSize = false;
      container.removeAttribute('aria-busy');
      compact = document.querySelector('#ProductGridContainer .rwady-page-controls')?.dataset.pageSize === '100';
      sync();
    }
  }
  function positionToggle() {
    const viewport = window.visualViewport;
    const offsetX = viewport?.offsetLeft || 0;
    const offsetY = viewport?.offsetTop || 0;
    const viewportWidth = viewport?.width || window.innerWidth;
    const viewportHeight = viewport?.height || window.innerHeight;
    const mobile = window.innerWidth <= 767;
    const tablet = window.innerWidth >= 768 && window.innerWidth <= 1023;
    const width = toggleSize?.width || toggle.offsetWidth || (mobile ? 44 : 160);
    const height = toggleSize?.height || toggle.offsetHeight || 44;
    const safeBottom = parseFloat(getComputedStyle(toggle).getPropertyValue('--browse-safe-bottom')) || 0;
    const minX = offsetX + 12;
    const maxX = Math.max(minX, offsetX + viewportWidth - width - 12);
    const minY = offsetY + 12;
    const maxY = Math.max(minY, offsetY + viewportHeight - height - (mobile ? 100 : tablet ? 92 : 24) - safeBottom);
    const cart = document.querySelector('[data-rwady-floating-cart]');
    const cartBox = cart?.getBoundingClientRect();
    const rect = cartBox?.width && cartBox?.height ? cartBox : null;
    const wa = rect ? null : document.querySelector('.rwady-whatsapp-float')?.getBoundingClientRect();
    let left = rect?.left ?? (wa && wa.left > offsetX + viewportWidth / 2 ? minX : maxX);
    let top = rect ? rect.bottom + 8 : maxY;
    if (rect && top > maxY) top = rect.top - height - 8;
    const setPosition = (property, value) => {
      if (!optimizeLayout || toggle.style.getPropertyValue(property) !== value) {
        toggle.style.setProperty(property, value);
      }
    };
    setPosition('--rwady-browse-left', Math.max(minX, Math.min(left, maxX)) + 'px');
    setPosition('--rwady-browse-top', Math.max(minY, Math.min(top, maxY)) + 'px');
    if (optimizeLayout) {
      positioned = true;
      toggle.style.visibility = '';
    }
  }
  const cards = () => document.querySelectorAll('#ProductGridContainer .rwady-compact-card');
  const sync = () => {
    scheduled = false;
    const available = cards().length > 0;
    const active = compact && available;
    const hidden = (!available && !changingPageSize) || modalOpen;
    const visibilityChanged = toggle.hidden !== hidden;
    const label = changingPageSize ? toggle.dataset.loadingLabel
      : loadFailed ? toggle.dataset.errorLabel
      : compact ? toggle.dataset.normalLabel : toggle.dataset.compactLabel;
    toggle.disabled = changingPageSize;
    toggle.setAttribute('aria-busy', String(changingPageSize));
    const labelChanged = toggle.querySelector('span').textContent !== label;
    if (!optimizeLayout || root.classList.contains('rwady-compact-browse') !== active) {
      root.classList.toggle('rwady-compact-browse', active);
      document.dispatchEvent(new CustomEvent('rwady:browse-mode-changed'));
    }
    if (!optimizeLayout || toggle.hidden !== hidden) toggle.hidden = hidden;
    // Measure in the following frame, after visibility/layout writes have settled.
    if (optimizeLayout) {
      if (!hidden && (!positioned || visibilityChanged || labelChanged)) queuePosition();
    } else positionToggle();
    toggle.setAttribute('aria-pressed', String(compact));
    if (labelChanged) toggle.querySelector('span').textContent = label;
    toggle.setAttribute('aria-label', label);
    toggle.title = label;
  };
  const queueSync = () => {
    if (!scheduled) { scheduled = true; requestAnimationFrame(sync); }
  };
  toggle.addEventListener('click', () => {
    const anchor = Array.from(cards()).map(el => el.closest('.product-card__wrapper'))
      .find(el => el.getBoundingClientRect().bottom > 160);
    const top = anchor?.getBoundingClientRect().top;
    if (changingPageSize) return;
    compact = !compact;
    sync();
    if (useQuickBrowsePageSize()) return;
    document.dispatchEvent(new CustomEvent('rwady:browse-mode-changed'));
    if (anchor) requestAnimationFrame(() => window.scrollBy({top: anchor.getBoundingClientRect().top - top, behavior: 'instant'}));
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('.rwady-compact-card');
    if (!compact || !button || !button.closest('#ProductGridContainer')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const view = window.Alpine?.store('xQuickView');
    if (!view?.load || !document.getElementById('QuickViewContainer')) {
      window.location.assign(button.dataset.quickProductUrl);
      return;
    }
    origin = { button, x: window.scrollX, y: window.scrollY };
    view.buttonQuickView = button;
    const eye = button.closest('.product-card__wrapper')?.querySelector('.rwady-product-card__quickview');
    // Use exactly the same action as the normal card's eye button.
    if (eye) eye.click();
    else if (view.load(button.dataset.quickProductId, button.dataset.quickProductUrl, button)) view.open();
  }, true);
  const watchModal = () => {
    window.Alpine.effect(() => {
      const view = window.Alpine.store('xQuickView');
      const open = !!view?.show;
      if (modalOpen && !open && origin) {
        const previous = origin;
        origin = null;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (previous.button.isConnected) previous.button.focus({preventScroll: true});
          window.scrollTo({left: previous.x, top: previous.y, behavior: 'instant'});
        }));
      }
      modalOpen = open;
      const hidden = (!cards().length && !changingPageSize) || open;
      const visibilityChanged = toggle.hidden !== hidden;
      if (!optimizeLayout || visibilityChanged) toggle.hidden = hidden;
      if (optimizeLayout && !hidden && (!positioned || visibilityChanged)) queuePosition();
    });
  };
  if (window.Alpine) watchModal();
  else document.addEventListener('alpine:initialized', watchModal, {once:true});
  if (optimizeLayout) {
    // Facets and quick browse replace the grid's children, not the container.
    // Observing the whole body made every unrelated header, app and pixel DOM
    // update walk its added nodes while the collection was loading.
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      new MutationObserver(queueSync).observe(gridContainer, {childList:true, subtree:true});
    }
    // The floating controls can change visibility without changing size.
    // Keep their position in sync without observing unrelated page mutations.
    const positionObserver = new MutationObserver(queuePosition);
    const cartControl = document.querySelector('[data-rwady-floating-cart]');
    const whatsappControl = document.querySelector('.rwady-whatsapp-float');
    for (const control of [cartControl, whatsappControl]) {
      if (control) positionObserver.observe(control, {attributes:true, attributeFilter:['class', 'style', 'hidden']});
    }
  } else {
    new MutationObserver(queueSync).observe(document.body, {childList:true, subtree:true});
  }
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      compact = document.querySelector('#ProductGridContainer .rwady-page-controls')?.dataset.pageSize === '100';
      changingPageSize = false;
      origin = null;
    }
    if (optimizeLayout) positioned = false;
    sync();
  });
  const queueLayout = optimizeLayout ? queuePosition : queueSync;
  window.addEventListener('resize', queueLayout);
  if (!optimizeLayout) window.addEventListener('scroll', queueLayout, {passive: true});
  window.visualViewport?.addEventListener('resize', queueLayout);
  window.visualViewport?.addEventListener('scroll', queueLayout, {passive: true});
  if (optimizeLayout && window.ResizeObserver) {
    new ResizeObserver(([entry]) => {
      const box = Array.isArray(entry?.borderBoxSize) ? entry.borderBoxSize[0] : entry?.borderBoxSize;
      const width = box?.inlineSize || entry?.contentRect?.width;
      const height = box?.blockSize || entry?.contentRect?.height;
      if (!width || !height || (toggleSize?.width === width && toggleSize?.height === height)) return;
      toggleSize = { width, height };
      queuePosition();
    }).observe(toggle);
  }
  const floatingCart = document.querySelector('[data-rwady-floating-cart]');
  if (floatingCart && window.ResizeObserver) new ResizeObserver(queueLayout).observe(floatingCart);
  sync();
})();



